import React, { MouseEvent, useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Switch, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { StyledLoadingButton } from '../../../../components/forms/LoadingButton';
import getTimeFormatFromLocale from '../../../../utils/getTimeFormatFromLocale';
import { getUrls } from '../../../../utils/url';
import ApiKeysContainer from '../../ApiKeysContainer';
import Scopes from './FormComponents/Scopes';
import KeysBreadCrumbs from './KeysBreadCrumbs';
import RegenerateKey from './RegenerateKey';
import { AdminControlCheckbox } from '../../utils/AdminControlCheckbox';

const ButtonsDiv = styled.div`
    margin-top: 24px;
    display: flex;
    justify-content: flex-start;
`;

const ItemDisplayDiv = styled.div`
    margin-top: 12px;
`;

interface ItemDisplayProps {
    label: string;
    value: string;
}

const ItemDisplay: React.FC<ItemDisplayProps> = ({ label, value }) => (
    <ItemDisplayDiv>
        <Typography variant="body1">{label}</Typography>
        <Typography variant="body2">{value}</Typography>
    </ItemDisplayDiv>
);

const KeyEdit: React.FC = () => {
    const { t } = useTranslation('admin_settings');
    const { selectedKey, formActionType } = useContainer(ApiKeysContainer);
    const [formValues, setFormValues] = useState<any>(selectedKey);
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
    const { updateApiKey, deleteApiKey, isDeleting, isSaving } = useContainer(ApiKeysContainer);
    const { path } = useRouteMatch();
    const history = useHistory();
    const { api: baseApiUrl } = getUrls();

    const keyName = formActionType === 'CREATE' ? t('api-keys.new-api-key') : selectedKey?.name;

    const { format: timeFormat } = getTimeFormatFromLocale();
    const expirationDate = formValues.expires_at
        ? format(new Date(formValues.expires_at), timeFormat)
        : t('api-keys.no-expiration');

    const handleScopesChange = (event) => {
        const {
            target: { name, checked }
        } = event;
        let newScopes = formValues.scopes;
        if (checked) {
            newScopes.push(name);
            if (name.includes('write')) {
                const fieldRead = name.replace('write', 'read');
                if (!newScopes.includes(fieldRead)) {
                    newScopes.push(fieldRead);
                }
            }
        } else {
            newScopes = newScopes.filter((scope) => scope !== name);
        }
        setFormValues({
            ...formValues,
            scopes: newScopes
        });
    };

    const handleIsActiveChange = (event) => {
        const {
            target: { checked }
        } = event;
        setFormValues({
            ...formValues,
            is_active: checked
        });
    };

    const handleSave = async (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        const expirationSave = formValues.expires_at?.includes('no-expiration') ? null : formValues.expires_at;
        await updateApiKey({
            ...formValues,
            expires_at: expirationSave
        });
        history.push(path.replace('/edit', ''));
    };

    const handleDelete = async (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        await deleteApiKey(formValues);
        setOpenDeleteDialog(false);
        history.push(path.replace('/edit', ''));
    };

    const renderDeleteDialog = () => (
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
            <DialogTitle>{t('api-keys.delete-key-title')}</DialogTitle>
            <DialogContent>{t('api-keys.delete-key-warning')}</DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined">
                    {t('cancel')}
                </Button>
                <StyledLoadingButton
                    sx={{ marginLeft: '10px' }}
                    onClick={handleDelete}
                    loading={isDeleting}
                    variant="contained"
                    color="error"
                >
                    {t('api-keys.delete-key')}
                </StyledLoadingButton>
            </DialogActions>
        </Dialog>
    );
    return (
        <div>
            <Typography variant="h1">{t('api-keys.edit-api-key')}</Typography>
            <KeysBreadCrumbs keyName={keyName} />
            <RegenerateKey />
            <ItemDisplay label={t('api-keys.key-name')} value={formValues.name} />
            <ItemDisplay label={t('api-keys.key-description')} value={formValues.description} />
            <ItemDisplay label={t('api-keys.form-expiresAt')} value={expirationDate} />
            <ItemDisplay label={t('api-keys.base-api-url')} value={baseApiUrl} />
            <Scopes scopes={formValues.scopes} handleChange={handleScopesChange} />
            <AdminControlCheckbox
                sx={{ marginTop: '24px' }}
                label={t('api-keys.enable-key')}
                control={
                    <Switch
                        name="is_active"
                        value={formValues.is_active}
                        checked={formValues.is_active}
                        onChange={handleIsActiveChange}
                    />
                }
            />
            <ButtonsDiv>
                <Button onClick={() => setOpenDeleteDialog(true)} variant="contained" color="error">
                    {t('api-keys.delete-key')}
                </Button>
                <StyledLoadingButton
                    sx={{ padding: 'auto 12px', marginLeft: '10px' }}
                    onClick={handleSave}
                    loading={isSaving}
                    variant="contained"
                    color="primary"
                >
                    {t('save')}
                </StyledLoadingButton>
            </ButtonsDiv>
            {renderDeleteDialog()}
        </div>
    );
};

export default KeyEdit;
